import { toMl } from './units.js';
import type { RecipeLine, Unit, VersionDetail } from './types.js';

export type LineChange = 'same' | 'added' | 'removed' | 'changed';

export interface LineDiff {
  ingredientId: string;
  ingredientName: string;
  a: { amount: number; unit: Unit } | null;
  b: { amount: number; unit: Unit } | null;
  change: LineChange;
}

export interface FieldDiff {
  field: 'technique' | 'glass' | 'ice' | 'garnish' | 'methodNotes';
  a: string;
  b: string;
  changed: boolean;
}

export interface VersionDiff {
  lines: LineDiff[];
  fields: FieldDiff[];
  abvDelta: number | null;
  volumeDelta: number | null;
}

type Comparable = Pick<
  VersionDetail,
  'technique' | 'glass' | 'ice' | 'garnish' | 'methodNotes' | 'abvEst' | 'volumeEst'
> & { lines: Pick<RecipeLine, 'ingredientId' | 'ingredientName' | 'amount' | 'unit'>[] };

/** Same quantity? oz and ml compare by volume so 2 oz == 59.1 ml is "same". */
function sameAmount(x: { amount: number; unit: Unit }, y: { amount: number; unit: Unit }): boolean {
  const mx = toMl(x.amount, x.unit);
  const my = toMl(y.amount, y.unit);
  if (mx !== null && my !== null) return Math.abs(mx - my) < 0.1;
  return x.unit === y.unit && Math.abs(x.amount - y.amount) < 1e-9;
}

/** Keys an ingredient by id plus occurrence, so an ingredient listed twice still lines up. */
function keyed(lines: Comparable['lines']) {
  const seen = new Map<string, number>();
  return lines.map((l) => {
    const n = seen.get(l.ingredientId) ?? 0;
    seen.set(l.ingredientId, n + 1);
    return { key: `${l.ingredientId}#${n}`, line: l };
  });
}

export function diffVersions(a: Comparable, b: Comparable): VersionDiff {
  const ka = keyed(a.lines);
  const kb = new Map(keyed(b.lines).map((x) => [x.key, x.line]));
  const lines: LineDiff[] = [];
  for (const { key, line } of ka) {
    const other = kb.get(key);
    kb.delete(key);
    const av = { amount: line.amount, unit: line.unit };
    const bv = other ? { amount: other.amount, unit: other.unit } : null;
    lines.push({
      ingredientId: line.ingredientId,
      ingredientName: line.ingredientName,
      a: av,
      b: bv,
      change: !bv ? 'removed' : sameAmount(av, bv) ? 'same' : 'changed',
    });
  }
  for (const line of kb.values()) {
    lines.push({
      ingredientId: line.ingredientId,
      ingredientName: line.ingredientName,
      a: null,
      b: { amount: line.amount, unit: line.unit },
      change: 'added',
    });
  }
  const fieldNames = ['technique', 'glass', 'ice', 'garnish', 'methodNotes'] as const;
  return {
    lines,
    fields: fieldNames.map((field) => ({
      field,
      a: a[field],
      b: b[field],
      changed: a[field] !== b[field],
    })),
    abvDelta: a.abvEst !== null && b.abvEst !== null ? b.abvEst - a.abvEst : null,
    volumeDelta: a.volumeEst !== null && b.volumeEst !== null ? b.volumeEst - a.volumeEst : null,
  };
}
